import { useEffect } from "react";
import type { Lang } from "./lang";
import { translateDbMessage } from "./fehler";

/**
 * Sprache der gemeinsamen Bausteine im Creator-Rahmen (Befund 2): Kopfzeile, Seitenleiste, Handy-Leiste, Befehlspalette,
 * Dialog-Knopf „Schließen“, Lade- und Fehlerzustände und Meldungen stammen aus Bausteinen, die beide Portale teilen und
 * (noch) keine Sprache kennen. Solange das so ist, übersetzt der Creator-Rahmen deren feste Texte und Vorleser-Beschriftungen
 * hier, nur im Creator-Portal und nur exakte, bekannte Texte (Inhalte wie Verträge oder Chat bleiben unberührt).
 * Unabhängig von der Sprache ersetzt er den Satz „Wende dich an deinen Admin“, den es für Creator nicht gibt (Befund 19).
 */

const SHELL_EN: Record<string, string> = {
  "Zum Inhalt": "Skip to content",
  "Agentur wechseln": "Switch agency",
  "Suche öffnen": "Open search",
  "Benachrichtigungen": "Notifications",
  "Hell- oder Dunkelmodus": "Light or dark mode",
  "Hauptnavigation": "Main navigation",
  "Mobile Navigation": "Mobile navigation",
  "Schnell zu …": "Go to …",
  "Schließen": "Close",
  "Meldung schließen": "Close message",
  "Seiten suchen": "Search pages",
  "Kooperationen, Rechnungen, Creator …": "Deals, invoices, contracts …",
  "Keine passenden Ergebnisse.": "No matching results.",
  "Wird geladen …": "Loading …",
  "Erneut versuchen": "Try again",
  "Zur Startseite": "Go to start page",
  "Das hat nicht geklappt.": "That did not work.",
  "Verbindung fehlgeschlagen.": "Connection failed.",
  "Deine Eingaben bleiben erhalten. Versuche es erneut.": "Your entries are kept. Please try again.",
  "Diese Seite ist nicht freigegeben.": "This page is not available to you.",
  "Das ist für dich nicht freigegeben.": "This is not available to you.",
  "Schreib deiner Agentur.": "Write to your agency.",
  "Schreib deiner Agentur, wenn du es brauchst.": "Write to your agency if you need it.",
  "Das ist für dich nicht freigegeben. Schreib deiner Agentur, wenn du es brauchst.": "This is not available to you. Write to your agency if you need it.",
  "Demo · Alle Daten sind erfunden.": "Demo · All data is fictitious.",
};

/** Creator haben keinen Admin: in beiden Sprachen durch den Weg über die Agentur ersetzen. */
const CREATOR_DE: Record<string, string> = {
  "Wende dich an deinen Admin.": "Schreib deiner Agentur.",
  "Diese Aktion ist nicht freigegeben.": "Das ist für dich nicht freigegeben.",
  "Diese Aktion ist nicht freigegeben. Wende dich an deinen Admin.": "Das ist für dich nicht freigegeben. Schreib deiner Agentur, wenn du es brauchst.",
};

const PATTERNS_EN: [RegExp, string][] = [
  [/^Benachrichtigungen, (\d+) neu$/, "Notifications, $1 new"],
  [/^(\d+) ungelesen$/, "$1 unread"],
];

/** Chat-Hinweis am Handy (Befund 18): dort beginnt Enter eine neue Zeile, gesendet wird per Knopf. */
const CHAT_ENTER = /^Enter (sendet|sends)[^.]*\.\s*/;
const CHAT_ENTER_TOUCH: Record<Lang, string> = {
  de: "Enter beginnt eine neue Zeile, gesendet wird mit dem Knopf „Senden“. ",
  en: "Enter starts a new line; send with the “Send” button. ",
};

/**
 * Übersetzung eines festen Textes; null, wenn er unbekannt ist oder schon passt. Meldungen der Datenbank nur in
 * Fehlerflächen (db), der Chat-Hinweis nur auf Touch-Geräten (touch).
 */
export function frameText(text: string, lang: Lang, opts: { db?: boolean; touch?: boolean } = {}): string | null {
  const core = text.trim();
  if (!core) return null;
  if (opts.touch && CHAT_ENTER.test(core) && !core.startsWith(CHAT_ENTER_TOUCH[lang].trim())) {
    return text.replace(CHAT_ENTER, CHAT_ENTER_TOUCH[lang]);
  }
  const creator = CREATOR_DE[core];
  const base = creator ?? core;
  if (lang === "en") {
    const shell = SHELL_EN[base];
    if (shell) return swap(text, core, shell);
    for (const [re, to] of PATTERNS_EN) if (re.test(base)) return swap(text, core, base.replace(re, to));
    // Meldungen der Datenbank in Toasts und Fehlerhinweisen
    if (opts.db) {
      const db = translateDbMessage(base, "en");
      if (db !== base) return swap(text, core, db);
    }
  }
  return creator ? swap(text, core, creator) : null;
}

function swap(text: string, core: string, to: string): string {
  return text.replace(core, to);
}

const ATTRS = ["aria-label", "placeholder", "title"] as const;
/** Inhalte, die nie übersetzt werden: Vertragspapier, Chatverläufe, Eingaben */
const SKIP = ".paper, [data-no-translate], textarea, [contenteditable='true'], .tk-messages";
/** Flächen, in denen Meldungen der Datenbank stehen können */
const ERROR_AREAS = ".toast, [role='alert'], .tk-error";

type Original = { text?: string; attrs?: Record<string, string> };
const originals = new WeakMap<Node, Original>();

function skipped(node: Node): boolean {
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return !!el?.closest(SKIP);
}

let touch = false;

function translateText(node: Text, lang: Lang) {
  if (skipped(node)) return;
  const value = node.nodeValue ?? "";
  const next = frameText(value, lang, { db: !!node.parentElement?.closest(ERROR_AREAS), touch });
  if (next === null || next === value) return;
  // Der zuletzt gesehene Originaltext zählt (React kann ihn inzwischen geändert haben)
  originals.set(node, { ...(originals.get(node) ?? {}), text: value });
  node.nodeValue = next;
}

function translateElement(el: Element, lang: Lang) {
  if (el.closest(SKIP)) return;
  for (const a of ATTRS) {
    const value = el.getAttribute(a);
    if (!value) continue;
    const next = frameText(value, lang);
    if (next === null || next === value) continue;
    const o = originals.get(el) ?? {};
    originals.set(el, { ...o, attrs: { ...(o.attrs ?? {}), [a]: value } });
    el.setAttribute(a, next);
  }
}

/** Übersetzt einen Teilbaum (Texte und Beschriftungen). */
export function translateTree(root: Node, lang: Lang): void {
  if (root.nodeType === Node.TEXT_NODE) {
    translateText(root as Text, lang);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  translateElement(root as Element, lang);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) translateText(n as Text, lang);
    else translateElement(n as Element, lang);
  }
}

/** Setzt übersetzte Texte zurück (Sprache wieder Deutsch oder Creator-Rahmen verlassen). */
export function restoreTree(root: Node): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  const visit = (n: Node) => {
    const o = originals.get(n);
    if (!o) return;
    if (o.text !== undefined && n.nodeType === Node.TEXT_NODE) n.nodeValue = o.text;
    if (o.attrs && n.nodeType === Node.ELEMENT_NODE) for (const [a, v] of Object.entries(o.attrs)) (n as Element).setAttribute(a, v);
    originals.delete(n);
  };
  visit(root);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) visit(n);
}

/**
 * Hält Rahmen, Palette, Dialoge und Meldungen in der Sprache des Portals, solange der Creator-Rahmen steht.
 * Setzt dazu die Sprache des Dokuments (Vorleser) und stellt beim Verlassen alles zurück.
 */
export function useRahmenSprache(lang: Lang): void {
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    const html = document.documentElement;
    const prevLang = html.lang;
    touch = !!window.matchMedia?.("(pointer: coarse)").matches;
    html.lang = lang;
    translateTree(document.body, lang);
    const observer = new MutationObserver((records) => {
      for (const r of records) {
        if (r.type === "childList") r.addedNodes.forEach((n) => translateTree(n, lang));
        else if (r.type === "characterData") translateText(r.target as Text, lang);
        else if (r.type === "attributes" && r.target.nodeType === Node.ELEMENT_NODE) translateElement(r.target as Element, lang);
      }
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTRS] });
    return () => {
      observer.disconnect();
      restoreTree(document.body);
      html.lang = prevLang || "de";
    };
  }, [lang]);
}
